import React, { useEffect, useRef, useState } from 'react';
import * as d3 from 'd3';
import './D3CandlestickChart.css';

const D3CandlestickChart = ({ chartData, baseline, hideHeader }) => {
  const [zoomed, setZoomed] = useState(false);
  const [resetBump, setResetBump] = useState(0);
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const zoomRef = useRef(null);

  useEffect(() => {
    zoomRef.current = null;  // reset zoom window on new data/range
    setZoomed(false);
    try { d3.select(containerRef.current).selectAll('.d3-tooltip').style('display', 'none'); } catch (e) {}
    const renderChart = () => {
      console.log('RenderChart called with data:', {
        hasChartData: !!chartData,
        loading: chartData?.loading,
        candlesLength: chartData?.candles?.length,
        dataLength: chartData?.data?.length
      });

      if (!chartData || chartData.loading) {
        console.log('Chart not rendering: loading or no data');
        return;
      }
      if (!containerRef.current || !svgRef.current) {
        console.log('Chart not rendering: refs not ready');
        return;
      }

      // Clear previous chart
      d3.select(svgRef.current).selectAll("*").remove();

      // Process data
      let data = [];
      if (chartData.candles && chartData.candles.length > 0) {
        // Real Schwab data
        console.log('Processing Schwab candles:', chartData.candles.length);
        data = chartData.candles.map(d => ({
          date: new Date(d.datetime),
          open: parseFloat(d.open) || 0,
          high: parseFloat(d.high) || 0,
          low: parseFloat(d.low) || 0,
          close: parseFloat(d.close) || 0,
          volume: parseInt(d.volume) || 0
        }));
        if (zoomRef.current && Number.isInteger(zoomRef.current.start)) {
          data = data.slice(zoomRef.current.start, zoomRef.current.end);
        }
      } else if (chartData.data && chartData.data.length > 0) {
        // Mock data - convert to candles
        const mockCandles = [];
        for (let i = 0; i < chartData.data.length; i += 5) {
          const slice = chartData.data.slice(i, Math.min(i + 5, chartData.data.length));
          const prices = slice.map(d => d.price);
          mockCandles.push({
            date: new Date(),
            open: prices[0],
            high: Math.max(...prices),
            low: Math.min(...prices),
            close: prices[prices.length - 1],
            volume: slice.reduce((sum, d) => sum + (d.volume || 0), 0)
          });
        }
        data = mockCandles;
      }

      if (data.length === 0) {
        console.log('No chart data available');
        return;
      }

      // Dimensions - with fallback values
      const margin = { top: 20, right: 80, bottom: 40, left: 50 };
      const containerWidth = containerRef.current.clientWidth || 600;
      const width = Math.max(containerWidth - margin.left - margin.right, 400);
      const height = 300; // Main chart height - compact for better space usage
      const volumeHeight = 60; // Volume bar height

      console.log('Chart dimensions:', { containerWidth, width, height, dataLength: data.length });

      // SVG setup - Total height includes chart + volume + margins + spacing
      const totalHeight = height + volumeHeight + margin.top + margin.bottom + 10; // Extra 10px for spacing between chart and volume
      const svg = d3.select(svgRef.current)
        .attr('width', width + margin.left + margin.right)
        .attr('height', totalHeight)
        .attr('viewBox', `0 0 ${width + margin.left + margin.right} ${totalHeight}`)
        .attr('preserveAspectRatio', 'xMidYMid meet');

      // Background
      svg.append('rect')
        .attr('width', width + margin.left + margin.right)
        .attr('height', totalHeight)
        .attr('fill', '#1a1a1f');

      const g = svg.append('g')
        .attr('transform', `translate(${margin.left},${margin.top})`);

      // Scales
      const xScale = d3.scaleBand()
        .domain(data.map((d, i) => i))
        .range([0, width])
        .padding(0.1);

      const yScale = d3.scaleLinear()
        .domain([
          d3.min(data, d => d.low) * 0.995,
          d3.max(data, d => d.high) * 1.005
        ])
        .range([height, 0]);

      const volumeScale = d3.scaleLinear()
        .domain([0, d3.max(data, d => d.volume)])
        .range([volumeHeight, 0]);

      // Grid lines
      const gridLines = g.append('g')
        .attr('class', 'grid');

      // Horizontal grid lines
      gridLines.selectAll('.horizontal-grid')
      .data(yScale.ticks(10))
      .enter().append('line')
      .attr('class', 'horizontal-grid')
      .attr('x1', 0)
      .attr('x2', width)
      .attr('y1', d => yScale(d))
      .attr('y2', d => yScale(d))
      .attr('stroke', '#2a2a2f')
      .attr('stroke-dasharray', '2,2')
      .attr('opacity', 0.3);

      // Vertical grid lines
      gridLines.selectAll('.vertical-grid')
      .data(d3.range(0, data.length, Math.ceil(data.length / 10)))
      .enter().append('line')
      .attr('class', 'vertical-grid')
      .attr('x1', d => xScale(d) + xScale.bandwidth() / 2)
      .attr('x2', d => xScale(d) + xScale.bandwidth() / 2)
      .attr('y1', 0)
      .attr('y2', height)
      .attr('stroke', '#2a2a2f')
      .attr('stroke-dasharray', '2,2')
      .attr('opacity', 0.3);

      // Draw support/resistance zones first (background)
      const priceRange = d3.max(data, d => d.high) - d3.min(data, d => d.low);
      const currentPrice = data[data.length - 1].close;

      // Support zone (below current price)
      const supportLevel = currentPrice - (priceRange * 0.15);
      g.append('rect')
        .attr('x', 0)
        .attr('y', yScale(currentPrice))
        .attr('width', width)
        .attr('height', yScale(supportLevel) - yScale(currentPrice))
        .attr('fill', '#10b981')
        .attr('opacity', 0.08);

      // Resistance zone (above current price)
      const resistanceLevel = currentPrice + (priceRange * 0.15);
      g.append('rect')
        .attr('x', 0)
        .attr('y', yScale(resistanceLevel))
        .attr('width', width)
        .attr('height', yScale(currentPrice) - yScale(resistanceLevel))
        .attr('fill', '#ef4444')
        .attr('opacity', 0.08);

      // Draw candlesticks (ThinkorSwim style)
      const candleGroup = g.append('g')
        .attr('class', 'candles');

      // Draw wicks (high-low lines)
      candleGroup.selectAll('.wick')
        .data(data)
        .enter().append('line')
        .attr('class', 'wick')
        .attr('x1', (d, i) => xScale(i) + xScale.bandwidth() / 2)
        .attr('x2', (d, i) => xScale(i) + xScale.bandwidth() / 2)
        .attr('y1', d => yScale(d.high))
        .attr('y2', d => yScale(d.low))
        .attr('stroke', d => d.close >= d.open ? '#10b981' : '#ef4444')
        .attr('stroke-width', 1);

      // Draw candle bodies (open-close rectangles)
      candleGroup.selectAll('.body')
        .data(data)
        .enter().append('rect')
        .attr('class', 'body')
        .attr('x', (d, i) => xScale(i))
        .attr('y', d => yScale(Math.max(d.open, d.close)))
        .attr('width', xScale.bandwidth())
        .attr('height', d => {
          const bodyHeight = Math.abs(yScale(d.open) - yScale(d.close));
          return Math.max(bodyHeight, 1); // Minimum 1px for doji candles
        })
        .attr('fill', d => d.close >= d.open ? '#10b981' : '#ef4444')
        .attr('stroke', d => d.close >= d.open ? '#10b981' : '#ef4444')
        .attr('stroke-width', 0.5);

      // Entry-price baseline (dashed) when provided (Trending option P&L view)
      if (baseline != null && isFinite(baseline)) {
        const by = yScale(baseline);
        if (isFinite(by)) {
          g.append('line')
            .attr('x1', 0).attr('x2', width)
            .attr('y1', by).attr('y2', by)
            .attr('stroke', '#8b94a3').attr('stroke-width', 1)
            .attr('stroke-dasharray', '4,3').attr('opacity', 0.75);
          g.append('text')
            .attr('x', 4).attr('y', by - 4)
            .attr('fill', '#8b94a3').attr('font-size', 9).attr('font-family', 'monospace')
            .text('entry $' + Number(baseline).toFixed(2));
        }
      }

      // Volume bars - Position them below the main chart with some spacing
      const volumeGroup = g.append('g')
        .attr('transform', `translate(0, ${height + 15})`);

      volumeGroup.selectAll('.volume-bar')
        .data(data)
        .enter().append('rect')
        .attr('class', 'volume-bar')
        .attr('x', (d, i) => xScale(i))
        .attr('y', d => volumeScale(d.volume))
        .attr('width', xScale.bandwidth())
        .attr('height', d => volumeHeight - volumeScale(d.volume))
        .attr('fill', d => d.close >= d.open ? '#10b981' : '#ef4444')
        .attr('opacity', 0.6);

      // Moving averages (EMA 9 and EMA 21) with curved lines
      if (data.length > 9) {
      const ema9 = calculateEMA(data.map(d => d.close), 9);
      const ema21 = calculateEMA(data.map(d => d.close), 21);

      const line9 = d3.line()
        .x((d, i) => xScale(i + (data.length - ema9.length)) + xScale.bandwidth() / 2)
        .y(d => yScale(d))
        .curve(d3.curveCatmullRom.alpha(0.5)); // Smooth curve like in target

      g.append('path')
        .datum(ema9)
        .attr('class', 'ema-9')
        .attr('fill', 'none')
        .attr('stroke', '#fbbf24')
        .attr('stroke-width', 2)
        .attr('d', line9);

      if (data.length > 21) {
        const line21 = d3.line()
          .x((d, i) => xScale(i + (data.length - ema21.length)) + xScale.bandwidth() / 2)
          .y(d => yScale(d))
          .curve(d3.curveCatmullRom.alpha(0.5)); // Smooth curve like in target

        g.append('path')
          .datum(ema21)
          .attr('class', 'ema-21')
          .attr('fill', 'none')
          .attr('stroke', '#8b5cf6')
          .attr('stroke-width', 2)
          .attr('d', line21);
      }
      }

      // Previous close line
      if (chartData.previousClose) {
      g.append('line')
        .attr('class', 'prev-close')
        .attr('x1', 0)
        .attr('x2', width)
        .attr('y1', yScale(chartData.previousClose))
        .attr('y2', yScale(chartData.previousClose))
        .attr('stroke', '#fbbf24')
        .attr('stroke-width', 1)
        .attr('stroke-dasharray', '5,5')
        .attr('opacity', 0.5);
      }

      // X-axis - detect if multi-day data
      const firstDate = data[0]?.date;
      const lastDate = data[data.length - 1]?.date;
      const isMultiDay = firstDate && lastDate &&
        (lastDate.getTime() - firstDate.getTime()) > 24 * 60 * 60 * 1000; // More than 24 hours

      const xAxis = d3.axisBottom(xScale)
      .tickValues(d3.range(0, data.length, Math.ceil(data.length / 10)))
      .tickFormat(i => {
        if (data[i]) {
          const date = data[i].date;
          // For multi-day charts, show date; for intraday, show time only
          if (isMultiDay) {
            return d3.timeFormat('%m/%d %H:%M')(date);
          }
          return d3.timeFormat('%H:%M')(date);
        }
        return '';
      });

      g.append('g')
      .attr('transform', `translate(0,${height})`)
      .attr('class', 'x-axis')
      .call(xAxis)
      .selectAll('text')
      .style('fill', '#9ca3af')
      .style('font-size', '10px');

      // Y-axis
      const yAxis = d3.axisRight(yScale)
      .tickSize(0)
      .tickPadding(10)
      .tickFormat(d => `$${d.toFixed(2)}`);

      g.append('g')
      .attr('transform', `translate(${width},0)`)
      .attr('class', 'y-axis')
      .call(yAxis)
      .selectAll('text')
      .style('fill', '#9ca3af')
      .style('font-size', '10px');

      // Crosshair
      const crosshairGroup = g.append('g')
      .attr('class', 'crosshair')
      .style('display', 'none');

      crosshairGroup.append('line')
      .attr('class', 'crosshair-x')
      .attr('stroke', '#4a4a4f')
      .attr('stroke-width', 1)
      .attr('stroke-dasharray', '3,3');

      crosshairGroup.append('line')
      .attr('class', 'crosshair-y')
      .attr('stroke', '#4a4a4f')
      .attr('stroke-width', 1)
      .attr('stroke-dasharray', '3,3');

      // Price label
      const priceLabel = crosshairGroup.append('g')
      .attr('class', 'price-label');

      priceLabel.append('rect')
      .attr('fill', '#3a3a3f')
      .attr('rx', 3);

      priceLabel.append('text')
      .attr('fill', '#ffffff')
      .attr('font-size', '11px')
      .attr('text-anchor', 'middle');

      // Tooltip
      const tooltip = d3.select(containerRef.current)
      .append('div')
      .attr('class', 'd3-tooltip')
      .style('display', 'none');

      // Add zoom info text
      svg.append('text')
      .attr('x', containerWidth - 10)
      .attr('y', 20)
      .attr('text-anchor', 'end')
      .attr('font-size', '10px')
      .attr('fill', '#6b7280')
      .text('Drag to zoom · double-click or ⟲ to reset');

      // Mouse events
      const overlay = svg.append('rect')
      .attr('transform', `translate(${margin.left},${margin.top})`)
      .attr('width', width)
      .attr('height', height)
      .attr('fill', 'none')
      .attr('pointer-events', 'all');

      // Zoom functionality variables
      let zoomStartX = null;
      let zoomEndX = null;
      const zoomRect = g.append('rect')
        .attr('class', 'zoom-selection')
        .style('fill', 'rgba(96, 165, 250, 0.2)')
        .style('stroke', '#60a5fa')
        .style('stroke-width', 1)
        .style('display', 'none');

      overlay
      .on('mousedown', function(event) {
        event.preventDefault();
        const [x] = d3.pointer(event, this);
        zoomStartX = x;
        zoomRect
          .attr('x', x)
          .attr('y', 0)
          .attr('width', 0)
          .attr('height', height)
          .style('display', null);
      })
      .on('mousemove', function(event) {
        if (zoomStartX !== null) {
          const [x] = d3.pointer(event, this);
          const minX = Math.min(zoomStartX, x);
          const maxX = Math.max(zoomStartX, x);
          zoomRect
            .attr('x', minX)
            .attr('width', maxX - minX);
        } else {
        const [mouseX, mouseY] = d3.pointer(event, this);
        const bandWidth = xScale.bandwidth();
        const index = Math.floor(mouseX / (width / data.length));

        if (index >= 0 && index < data.length) {
          const d = data[index];

          crosshairGroup.style('display', null);

          crosshairGroup.select('.crosshair-x')
            .attr('x1', 0)
            .attr('x2', width)
            .attr('y1', mouseY)
            .attr('y2', mouseY);

          crosshairGroup.select('.crosshair-y')
            .attr('x1', xScale(index) + bandWidth / 2)
            .attr('x2', xScale(index) + bandWidth / 2)
            .attr('y1', 0)
            .attr('y2', height);

          const price = yScale.invert(mouseY);
          priceLabel.attr('transform', `translate(${width + 10}, ${mouseY})`);
          priceLabel.select('text').text(`$${price.toFixed(2)}`);
          const bbox = priceLabel.select('text').node().getBBox();
          priceLabel.select('rect')
            .attr('x', bbox.x - 4)
            .attr('y', bbox.y - 2)
            .attr('width', bbox.width + 8)
            .attr('height', bbox.height + 4);

          tooltip.style('display', 'block')
            .style('left', `${event.offsetX + 10}px`)
            .style('top', `${event.offsetY - 10}px`)
            .html(`
              <div class="tooltip-time">${d3.timeFormat('%H:%M')(d.date)}</div>
              <div>Open: $${d.open.toFixed(2)}</div>
              <div>High: $${d.high.toFixed(2)}</div>
              <div>Low: $${d.low.toFixed(2)}</div>
              <div>Close: $${d.close.toFixed(2)}</div>
              <div>Volume: ${(d.volume / 1000).toFixed(0)}K</div>
            `);
        }
        }
      })
      .on('mouseup', function(event) {
        if (zoomStartX !== null) {
          const [x] = d3.pointer(event, this);
          zoomEndX = x;

          // Apply zoom if selection is wide enough
          if (Math.abs(zoomEndX - zoomStartX) > 8) {
            const localStart = Math.max(0, Math.floor(Math.min(zoomStartX, zoomEndX) / (width / data.length)));
            const localEnd = Math.min(data.length, Math.ceil(Math.max(zoomStartX, zoomEndX) / (width / data.length)));
            if (localEnd - localStart >= 2) {
              const base = (zoomRef.current && Number.isInteger(zoomRef.current.start)) ? zoomRef.current.start : 0;
              zoomRef.current = { start: base + localStart, end: base + localEnd };
              setZoomed(true);
              try { tooltip.style('display', 'none'); crosshairGroup.style('display', 'none'); } catch (e) {}
              renderChart();
            }
          }

          // Reset zoom selection
          zoomStartX = null;
          zoomRect.style('display', 'none');
        }
      })
      .on('mouseout', function() {
        if (zoomStartX === null) {
          crosshairGroup.style('display', 'none');
          tooltip.style('display', 'none');
        }
      })
      .on('dblclick', function() {
        // Reset zoom on double-click
        if (zoomRef.current) {
          zoomRef.current = null;
          setZoomed(false);
          try { tooltip.style('display', 'none'); } catch (e) {}
          renderChart();
        }
      });
    };

    // Initial render with a small delay to ensure container is visible
    const timeoutId = setTimeout(() => {
      renderChart();
    }, 100);

    // Add resize observer to re-render when container size changes
    const resizeObserver = new ResizeObserver((entries) => {
      // Use requestAnimationFrame to avoid ResizeObserver loop errors
      window.requestAnimationFrame(() => {
        if (!entries || !entries.length) return;

        for (let entry of entries) {
          if (entry.contentRect.width > 0) {
            console.log('Container resized, re-rendering chart');
            renderChart();
          }
        }
      });
    });

    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    // Cleanup
    return () => {
      clearTimeout(timeoutId);
      if (containerRef.current) {
        resizeObserver.unobserve(containerRef.current);
      }
      // Clean up tooltip if it exists
      d3.select(containerRef.current).select('.d3-tooltip').remove();
    };
  }, [chartData, baseline, resetBump]);

  // Calculate exponential moving average
  function calculateEMA(prices, period) {
    const multiplier = 2 / (period + 1);
    const ema = [];

    // Start with SMA for first period
    let sum = 0;
    for (let i = 0; i < period && i < prices.length; i++) {
      sum += prices[i];
      if (i === period - 1) {
        ema.push(sum / period);
      } else {
        ema.push(null);
      }
    }

    // Calculate EMA for rest
    for (let i = period; i < prices.length; i++) {
      const previousEma = ema[i - 1] || prices[i];
      ema.push((prices[i] - previousEma) * multiplier + previousEma);
    }

    return ema.filter(v => v !== null);
  }

  if (chartData?.loading) {
    return (
      <div className="d3-chart-loading">
        <div className="d3-spinner"></div>
        <p>Loading chart data...</p>
      </div>
    );
  }

  if (chartData?.error) {
    return (
      <div className="d3-chart-error">
        <p>{chartData.error}</p>
      </div>
    );
  }

  // Debug information
  console.log('D3CandlestickChart render:', {
    hasChartData: !!chartData,
    loading: chartData?.loading,
    hasCandles: chartData?.candles?.length > 0,
    hasData: chartData?.data?.length > 0,
    error: chartData?.error
  });

  return (
    <div className="d3-chart-container" ref={containerRef} style={{ position: 'relative' }}>
      {zoomed && (
        <button onClick={() => { setZoomed(false); setResetBump((b) => b + 1); }} style={{
          position: 'absolute', top: hideHeader ? 8 : 38, right: 10, zIndex: 6,
          background: 'rgba(16,21,31,.92)', border: '1px solid rgba(251,191,36,.45)',
          color: '#fbbf24', borderRadius: 8, padding: '5px 11px', fontSize: 12,
          fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
          boxShadow: '0 4px 14px rgba(0,0,0,.4)',
        }}>⟲ Full view</button>
      )}
      {!hideHeader && <div className="chart-header-compact">
        <span className="chart-stat">
          ${chartData?.currentPrice?.toFixed(2) || 'N/A'}
        </span>
        <span className="chart-stat-divider">|</span>
        <span className="chart-stat">
          Prev: ${chartData?.previousClose?.toFixed(2) || chartData?.prevClose?.toFixed(2) || 'N/A'}
        </span>
        <span className="chart-stat-divider">|</span>
        <span className="chart-stat high">
          H: ${chartData?.dayHigh?.toFixed(2) || chartData?.high?.toFixed(2) || 'N/A'}
        </span>
        <span className="chart-stat low">
          L: ${chartData?.dayLow?.toFixed(2) || chartData?.low?.toFixed(2) || 'N/A'}
        </span>
        <span className="chart-stat-divider">|</span>
        <span className="chart-stat">
          Vol: {chartData?.volume ? (chartData.volume / 1000000).toFixed(2) + 'M' : 'N/A'}
        </span>
      </div>}
      <svg ref={svgRef} style={{ width: '100%', height: 'auto', display: 'block' }}></svg>
      {(!chartData?.candles || chartData.candles.length === 0) && (!chartData?.data || chartData.data.length === 0) && !chartData?.loading && (
        <div style={{ textAlign: 'center', padding: '20px', color: '#9ca3af' }}>
          No chart data available
        </div>
      )}
    </div>
  );
};

export default D3CandlestickChart;